import { ImpersonationParty, ImpersonationStart } from '@global-apis/impersonation';

/**
 * Local session handling for "view as user".
 *
 * The impersonation token is written to the ordinary `__token` key so that every
 * existing consumer -- the axios interceptors and the Apollo auth link -- picks it up
 * unchanged. The operator's own session is stashed alongside it and restored on exit.
 *
 * The identity keys have to move with the token: `checkCompanyId` in api/config.ts
 * invalidates the session when the token's company_id disagrees with `__company_id`,
 * so viewing a user in another company would otherwise log the operator straight out.
 */

const ORIGIN_KEY = '__impersonation-origin';
const SESSION_KEY = '__impersonation';

const IDENTITY_KEYS = ['__token', '__refresh-token', '__company_id', '__company_name', '__user_id', '__user_email'];

export interface ImpersonationSession {
  sessionId: number;
  target: ImpersonationParty;
  actorEmail: string;
  expiresAt: number;
}

// Declared rather than an arrow: esbuild applies the TSX loader here, where a bare
// <T> type parameter on an arrow function parses as a JSX element and fails the build.
function readJSON<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

export const getImpersonationSession = (): ImpersonationSession | null => readJSON<ImpersonationSession>(SESSION_KEY);

export const isImpersonating = (): boolean => getImpersonationSession() !== null;

/** Swap the operator's session for the target's, keeping the operator's to restore. */
export const beginImpersonation = (data: ImpersonationStart) => {
  const origin: Record<string, string | null> = {};
  for (const key of IDENTITY_KEYS) {
    origin[key] = localStorage.getItem(key);
  }
  localStorage.setItem(ORIGIN_KEY, JSON.stringify(origin));

  localStorage.setItem('__token', data.access);
  // No refresh token is issued for an impersonated session, and the periodic refresh
  // is skipped while one is active.
  localStorage.removeItem('__refresh-token');
  localStorage.setItem('__company_id', data.target.company_id?.toString() ?? '');
  localStorage.setItem('__company_name', data.target.company_name ?? '');
  localStorage.setItem('__user_id', data.target.id.toString());
  localStorage.setItem('__user_email', data.target.email);

  const session: ImpersonationSession = {
    sessionId: data.session_id,
    target: data.target,
    actorEmail: data.actor.email,
    expiresAt: Date.now() + data.expires_in * 1000
  };
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
};

/** Put the operator's own session back. Safe to call when nothing is stashed. */
export const restoreOperatorSession = () => {
  const origin = readJSON<Record<string, string | null>>(ORIGIN_KEY);

  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(ORIGIN_KEY);

  if (!origin) {
    return false;
  }

  for (const key of IDENTITY_KEYS) {
    const value = origin[key];
    if (value === null || value === undefined) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, value);
    }
  }

  return true;
};

export const impersonationHasExpired = (session: ImpersonationSession | null): boolean =>
  session !== null && Date.now() >= session.expiresAt;
